import React, { useEffect, useState } from "react";
import { getTermGroups, deleteTermGroup } from "@/api/termGroups";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Edit, Plus } from "lucide-react";
import TermGroupForm from "./TermGroupForm";

export default function TermGroupsIndex() {
    const [termGroups, setTermGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTermGroup, setEditingTermGroup] = useState(null);

    useEffect(() => {
        fetchTermGroups();
    }, []);

    const fetchTermGroups = async () => {
        setLoading(true);
        const data = await getTermGroups();
        if (data) {
            setTermGroups(data);
        }
        setLoading(false);
    };

    const handleDelete = async (termGroup) => {
        if (termGroup.termsCount > 0) {
            toast.error("Cannot delete term group that contains terms");
            return;
        }

        if (!confirm(`Are you sure you want to delete "${termGroup.name}"?`)) {
            return;
        }

        try {
            await deleteTermGroup(termGroup.id);
            toast.success("Term group deleted successfully");
            fetchTermGroups();
        } catch (error) {
            toast.error("Failed to delete term group");
        }
    };

    const handleEdit = (termGroup) => {
        setEditingTermGroup(termGroup);
        setShowForm(true);
    };

    const handleCreate = () => {
        setEditingTermGroup(null);
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingTermGroup(null);
        fetchTermGroups();
    };

    if (loading) {
        return <div className="p-6">Loading...</div>;
    }

    if (showForm) {
        return (
            <TermGroupForm
                termGroup={editingTermGroup}
                onClose={handleFormClose}
            />
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-[#5D1F42]">Term Groups</h1>
                <Button onClick={handleCreate} className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Create Term Group
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {termGroups.map((termGroup) => (
                    <Card key={termGroup.id} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center justify-between">
                                <span>{termGroup.name}</span>
                                <Badge variant="secondary">
                                    {termGroup.termsCount} terms
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {termGroup.description && (
                                <p className="text-gray-600 text-sm mb-4">
                                    {termGroup.description}
                                </p>
                            )}
                            
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEdit(termGroup)}
                                    className="flex items-center gap-2"
                                >
                                    <Edit className="h-3 w-3" />
                                    Edit
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDelete(termGroup)}
                                    disabled={termGroup.termsCount > 0}
                                    className="flex items-center gap-2"
                                >
                                    <Trash2 className="h-3 w-3" />
                                    Delete
                                </Button>
                            </div>
                            
                            <div className="mt-3 text-xs text-gray-500">
                                Created: {new Date(termGroup.createdAt).toLocaleDateString()}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {termGroups.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">No term groups found</p>
                    <Button onClick={handleCreate}>Create your first term group</Button>
                </div>
            )}
        </div>
    );
}